import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { PreviewBlockHelper } from '../../../../../utils/preview-block-helper';
import { GenVideoPreviewVideo } from '../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { PmaxPreviewData } from '../../../../../models/google-template/preview-interfaces/pmax-preview-data.interface';
import { PreviewSitelink } from '../../../../../models/google-template/preview-interfaces/preview-sitelink.interface';
import { YoutubeHelper } from '../../../../../utils/youtube-helper';
import { BasePreviewShellComponent } from '../preview-common/base-preview-shell.component';
import { PreviewVariant } from '../preview-common/preview-variant.interface';
import { PMAX_VARIANTS } from './variants/pmax-variants';

/**
 * Performance Max ad preview. The selector mechanics live in
 * {@link BasePreviewShellComponent}; this class supplies the variant registry
 * and how to build the flat {@link PmaxPreviewData} DTO from the blocks.
 *
 * To add a placement, register it in `PMAX_VARIANTS`.
 */
@Component({
  selector: 'app-pmax-preview',
  imports: [CommonModule, NgComponentOutlet],
  templateUrl: '../preview-common/base-preview-shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../preview-common/base-preview-shell.component.scss'
})
export class PmaxPreviewComponent extends BasePreviewShellComponent<PmaxPreviewData> {
  protected readonly registry: PreviewVariant[] = PMAX_VARIANTS;

  protected buildPreviewData(): PmaxPreviewData {
    return {
      longHeadlines: PreviewBlockHelper.collectFromBlock(this.blocks, 'pmax-long-headline', 'PmaxLongHeadline_', 5),
      headlines: PreviewBlockHelper.collectFromBlock(this.blocks, 'pmax-headline', 'PmaxHeadline_', 15),
      descriptions: PreviewBlockHelper.collectFromBlock(this.blocks, 'pmax-description', 'PmaxDescription_', 5),
      images: PreviewBlockHelper.collectFromBlock(this.blocks, 'pmax-image', 'PmaxImage_', 60),
      videos: this.toVideos(
        PreviewBlockHelper.collectFromBlock(this.blocks, 'pmax-youtube-url', 'PmaxYoutubeUrl_', 5),
      ),
      ctaText: PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'pmax-cta-text'), 'Text'),
      url: this.toAbsoluteUrl(
        PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'pmax-url'), 'Link'),
      ),
      sitelinks: this.buildSitelinks(),
    };
  }

  /** Every sitelink block with text, in editor order. */
  private buildSitelinks(): PreviewSitelink[] {
    return this.blocks
      .filter((b) => b.name === 'pmax-sitelink')
      .map((b) => ({
        text: PreviewBlockHelper.getVar(b, 'Text'),
        firstDescriptionLine: PreviewBlockHelper.getVar(b, 'FirstDescriptionLine'),
        secondDescriptionLine: PreviewBlockHelper.getVar(b, 'SecondDescriptionLine'),
        link: PreviewBlockHelper.getVar(b, 'SitelinkLink'),
      }))
      .filter((s) => s.text.trim().length > 0);
  }

  /** Pairs each link with its thumbnail, derived from the video id. */
  private toVideos(urls: string[]): GenVideoPreviewVideo[] {
    return urls.map((url) => {
      const videoId = YoutubeHelper.extractVideoId(url);
      return {
        url: this.toAbsoluteUrl(url),
        thumbnailUrl: videoId ? YoutubeHelper.thumbnailUrl(videoId) : '',
        videoId: videoId ?? '',
      };
    });
  }

  /** Prefix a scheme so an `[href]` does not resolve against the editor's origin. */
  private toAbsoluteUrl(rawUrl: string): string {
    const url = rawUrl.trim();
    if (!url) {
      return '';
    }
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }
}
